import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../../../contexts/ToastContext';
import { useAuth } from '../../../../contexts/AuthContext';
import { hasSuperAdminView } from '../../../../utils/rolePermissions';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export const useDgcaBaselineData = (aircraftType) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [baseline, setBaseline] = useState({
    // Duty Time / FDP Rules
    maxDailyFDPHours: 10,
    approachWarningThresholdHours: 8,
    allowExtensions: true,
    maxFdpExtensionHours: 2,
    maxFlightTimeExtensionHours: 1,
    restCompForExtension1h: 2,
    restCompForExtension2h: 4,
    // Helicopter-Specific FDP Rules
    maxDailyFDPHoursSinglePilot: 10,
    maxDailyFDPHoursTwoPilot1Landing: 13,
    maxDailyFDPHoursTwoPilot2Landings: 12.5,
    maxDailyFDPHoursTwoPilot3Landings: 12.5,
    maxFlightTimeTwoPilot1Landing: 7,
    maxFlightTimeTwoPilot2Landings: 7,
    maxFlightTimeTwoPilot3Landings: 7,
    // Helicopter-Specific: Flight Time Limits (PDF 6.1.2)
    maxFlightTime24Hours: 7,
    maxFlightTimeSinglePilot: 6,
    // Helicopter-Specific: Cumulative FDP Limits (PDF 6.1.1)
    maxFlightDutyPeriod7Days: 60,
    maxDutyTime28Days: 200,
    // Helicopter-Specific: Maximum Landings (PDF 6.2)
    maxLandingsPerDutyPeriod: 50,
    maxLandingsBreakRequiredRate: 10,
    maxLandingsBreakDuration: 30,
    // Split Duty Rules
    allowSplitDuty: true,
    minSplitDutyBreakHours: 3,
    maxSplitDutyBreakHours: 10,
    splitDutyAccommodationRequiredHours: 6,
    maxSplitDutySegmentHours: 10,
    // WOCL Rules
    woclStart: '02:00',
    woclEnd: '06:00',
    // Rest Period Rules
    minRestHoursStandard: 12,
    minRestHoursTimeZone1to3: 12,
    minRestHoursTimeZone4to7: 18,
    minRestHoursTimeZoneMoreThan7: 36,
    nightDutyExtraRestHours: 0,
    nightDutyWindowStart: '00:00',
    nightDutyWindowEnd: '06:00',
    // Cumulative Flight Time Limits
    maxFlightTime7Days: 35,
    maxFlightTime28Days: 100,
    maxFlightTimeAnnual: 1000,
    // Fixed Wing-Specific: Additional Cumulative Limits (Para 9)
    maxDutyPeriod7Days: 60,
    maxFlightTime14Days: 65,
    maxDutyPeriod14Days: 100,
    maxDutyPeriod28Days: 190,
    maxFlightTime90Days: 300,
    maxDutyPeriod90Days: 600,
    // Fixed Wing-Specific: Two Pilot Operation (Para 6)
    maxFlightTimeTwoPilot8Hrs: 8,
    maxFDPTwoPilot8Hrs5Landings: 11.5,
    maxFDPTwoPilot8Hrs4Landings: 12,
    maxFDPTwoPilot8Hrs3Landings: 12.5,
    maxFlightTimeTwoPilot10Hrs: 10,
    maxFDPTwoPilot10Hrs: 13.5,
    maxLandingsTwoPilot10Hrs: 2,
    // Fixed Wing-Specific: Augmented Crew (Para 7)
    maxFlightTime3Crew: 12,
    maxFlightTime3CrewGA: 13,
    maxFDP3CrewBunk: 17,
    maxFDP3CrewIsolatedRest: 16,
    maxFDP3CrewBasicRest: 15,
    maxLandings3Crew: 2,
    maxFlightTime4Crew: 14,
    maxFDP4CrewBunk: 18,
    maxFDP4CrewIsolatedRest: 17,
    maxLandings4Crew: 1,
    maxFlightTime4CrewULR: 14.1,
    maxFDP4CrewULRBunk: 19,
    maxLandings4CrewULR: 1,
    // Fixed Wing-Specific: Single Pilot Operation (Para 8)
    maxFlightTimeSinglePilot7Hrs: 7,
    maxFDPSinglePilot7Hrs: 8.5,
    maxLandingsSinglePilot7Hrs: 8,
    maxFlightTimeSinglePilot8Hrs: 8,
    maxFDPSinglePilot8Hrs: 9.5,
    maxLandingsSinglePilot8Hrs: 6,
    maxFlightTimePerSectorWithAutopilot: 4,
    maxFlightTimePerSectorWithoutAutopilot: 2,
    // Fixed Wing-Specific: Split Duty (Para 10)
    allowSplitDutyFixedWing: true,
    splitDutyBreakMinHours: 3,
    splitDutyBreakMaxHours: 10,
    splitDutyAccommodationRequiredHoursFixedWing: 6,
    maxSplitDutySegmentHoursFixedWing: 10,
    splitDutyBreakCountsAsFDP: true,
    // Fixed Wing-Specific: WOCL (Note 1)
    woclStartFixedWing: '02:00',
    woclEndFixedWing: '06:00',
    woclReductionStartEncroachment: 1.0,
    woclReductionEndEncroachment: 0.5,
    woclMaxReductionHours: 2,
    // Cumulative Duty Time Limits
    maxDutyTimeAnnual: 1800,
    // Weekly Rest Rules
    maxConsecutiveDutyDays: 6,
    weeklyRestHours: 48,
    requireTwoLocalNights: true,
    // Standby / Reserve Rules
    standbyCountsAsDuty: false,
    minReserveNoticeHours: 10,
    // Roster Publication
    minRosterPublicationDays: 7,
    requireWeeklyRestShown: true,
    // Record Keeping
    minRecordRetentionMonths: 18,
    // Fatigue Reporting
    enableNonPunitiveReporting: true,
    requireQuarterlyReport: true,
    fatigueReportRetentionMonths: 18,
    // Training
    requireAnnualFatigueTraining: true,
    trainingRenewalPeriodMonths: 12
  });
  const [isCustom, setIsCustom] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchBaseline = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/dgca-profile/baseline/config?aircraftType=${encodeURIComponent(aircraftType)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBaseline(response.data);
      setIsCustom(response.data.isCustom || false);
      setError('');
    } catch (error) {
      console.error('Error fetching baseline configuration:', error);
      setError('Failed to load baseline configuration. Please try again.');
      showToast('Failed to load baseline configuration', 'error');
    } finally {
      setLoading(false);
    }
  }, [aircraftType, showToast]);

  const fetchHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/dgca-profile/baseline/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching baseline history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    // SUPER_ADMIN and SPECTATOR may view baseline configuration
    if (user && !hasSuperAdminView(user.role)) {
      showToast('Access denied. Only SUPER_ADMIN can manage baseline configuration.', 'error');
      return;
    }
    fetchBaseline();
    fetchHistory();
  }, [user, aircraftType, fetchBaseline, fetchHistory, showToast]);

  return {
    baseline,
    setBaseline,
    isCustom,
    history,
    loading,
    historyLoading,
    error,
    setError,
    fetchBaseline,
    fetchHistory
  };
};
